import React from 'react';
import { Badge, Button, TooltipTrigger, SparkIcon } from '@spark';
import { EXAMPLE_STATUS_FILTERS, RESPONSE_STATUS_FILTERS } from '@/features/datasets/components/DataSelection/RawRecordsStatus/constants';
import { formatDateTime } from "@/shared/utils/helpers"
function StatusBadge({ success }) {
    const color = success ? '#52c41a' : (success === false ? '#ff4d4f' : '#faad14');
    const label = success ? 'Successful' : (success === false ? 'Failed' : 'Not Processed');
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <span style={{
                width: 6,
                height: 6,
                borderRadius: '50%',
                backgroundColor: color,
                display: 'inline-block',
            }} />
            <span>{label}</span>
        </div>
    );
}

/**
 * Create table columns configuration
 * @param {object} props - Properties for column configuration
 * @param {function} props.handleViewRawExample - Function to handle viewing raw example
 * @param {object} props.filters - Current filters state
 * @returns {Array} - Table columns configuration
 */
export const createColumns = ({ handleViewRawExample, filters }) => [
    {
        title: 'ID',
        dataIndex: 'id',
        key: 'id',
        width: '10%',
        ellipsis: true,
        render: (text) => (
            <TooltipTrigger text={text || ''} placement="top">
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'block' }}>{text}</span>
            </TooltipTrigger>
        ),
    },
    {
        title: 'Created At',
        dataIndex: 'timestamp',
        key: 'timestamp',
        width: '15%',
        render: (text) => formatDateTime(text),
        sorter: (a, b) => new Date(a.timestamp) - new Date(b.timestamp),
        sortDirections: ['descend', 'ascend'],
        defaultSortOrder: 'descend',
    },
    {
        title: 'Last Updated At',
        dataIndex: 'last_update_time',
        key: 'last_update_time',
        width: '15%',
        render: (text) => formatDateTime(text),
        sorter: (a, b) => new Date(a.last_update_time) - new Date(b.last_update_time),
        sortDirections: ['descend', 'ascend'],
    },
    {
        title: 'Example Extraction',
        dataIndex: 'example_success',
        key: 'example_success',
        width: '15%',
        render: (success) => <StatusBadge success={success} />,
        filters: EXAMPLE_STATUS_FILTERS,
        filteredValue: filters.exampleStatus ? [filters.exampleStatus] : null,
        onFilter: (value, record) => {
            if (value === 'success') return record.example_success === true;
            if (value === 'failed') return record.example_success === false;
            if (value === 'not_processed') return record.example_success === null;
            return true;
        },
    },
    {
        title: 'Response Extraction',
        dataIndex: 'response_success',
        key: 'response_success',
        width: '15%',
        render: (success) => <StatusBadge success={success} />,
        filters: RESPONSE_STATUS_FILTERS,
        filteredValue: filters.responseStatus ? [filters.responseStatus] : null,
        onFilter: (value, record) => {
            if (value === 'success') return record.response_success === true;
            if (value === 'failed') return record.response_success === false;
            if (value === 'not_processed') return record.response_success === null;
            return true;
        },
    },
    {
        title: 'Failure Reason',
        dataIndex: 'failure_reason',
        key: 'failure_reason',
        ellipsis: true,
        render: (_, record) => {
            const reason = record.example_failure_reason || record.response_failure_reason;
            return reason ? (
                <TooltipTrigger text={reason} placement="top">
                    <div style={{ maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {reason}
                    </div>
                </TooltipTrigger>
            ) : '0';
        },
    },
    {
        title: 'Actions',
        key: 'actions',
        width: '1%',
        render: (_, record) => (
            <Button
                variant="ghost"
                size="small"
                startIcon={<SparkIcon slug="show" size="small" />}
                onClick={() => handleViewRawExample(record)}
            >
                View
            </Button>
        ),
    },
];
